import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { useCart } from '../contexts/CartContext';
import { useToast } from '../components/common/Toast';
import { productsAPI } from '../services/api';
import type { Product } from '../types/product';
import { setProductSEO, clearProductSEO } from '../utils/productSEO';
import { useProductReviews } from '../hooks/useProductReviews';

import { ImageLightbox } from '../components/product/ImageLightbox';
import { StockUrgency } from '../components/product/StockUrgency';
import { TrustRow } from '../components/product/TrustRow';
import { PhotoReviewsPreview } from '../components/product/PhotoReviewsPreview';
import { SubmitReviewForm } from '../components/product/SubmitReviewForm';
import { FrequentlyBoughtTogether } from '../components/product/FrequentlyBoughtTogether';
import { BundleAndSave } from '../components/product/BundleAndSave';
import { TrustBadgesSection } from '../components/storefront/sections/TrustBadgesSection';
import { PaymentIconsSection } from '../components/storefront/sections/PaymentIconsSection';
import { Link } from 'react-router-dom';
import { formatPriceFromRinggit } from '../utils/currency';
import { ProductTypeSpecs, subscriptionInterval } from '../components/product/ProductTypeSpecs';

interface SingleProductPageProps {
  productId: number | string;
}

// Shared formatter, not `${currency} ${n}` — a product whose currency field
// says 'MYR' would otherwise render "MYR 320.00" where the rest of the
// storefront says "RM 320.00".
const fmt = (n: number, currency = 'MYR') =>
  formatPriceFromRinggit(n, true, currency);

const productImages = (p: Product): string[] => {
  if (Array.isArray(p.images) && p.images.length > 0) return p.images;
  if (typeof p.imageUrl === 'string') return [p.imageUrl];
  if (Array.isArray(p.imageUrl)) return p.imageUrl;
  return [];
};

const productPrice = (p: Product): number => {
  if (typeof p.price === 'number' && p.price > 0) return p.price;
  if (typeof (p as any).priceInt === 'number') return (p as any).priceInt / 100;
  return 0;
};

/**
 * Render-time fallback. Description is "too short" if under 60 chars
 * (one short tagline). Returns 3 generic but on-brand benefit bullets.
 */
const buildDefaultBenefits = (p: Product): string[] => {
  const desc = (p.description || '').trim();
  if (desc.length >= 60) return [];
  return [
    'Premium-quality build, made to last',
    'Fast, tracked shipping nationwide',
    '30-day satisfaction guarantee',
  ];
};

const DEFAULT_FAQS: Array<{ q: string; a: string }> = [
  {
    q: 'How long does shipping take?',
    a: 'Most orders ship within 1 business day. Standard delivery typically arrives in 2–5 working days, with tracking from the moment your order leaves our warehouse.',
  },
  {
    q: 'Can I return or exchange this?',
    a: 'Yes — you have 30 days from delivery to start a return or exchange, no questions asked, as long as the item is unused and in its original packaging.',
  },
  {
    q: 'Is there a warranty?',
    a: 'Every order is covered by our satisfaction guarantee. If anything goes wrong, reply to your order email and we\'ll make it right.',
  },
];

export const SingleProductPage: React.FC<SingleProductPageProps> = ({
  productId,
}) => {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { addItem, items: cartItems } = useCart();
  const cartCount = cartItems.reduce((s, it) => s + it.quantity, 0);
  const { showToast } = useToast();

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const reviewsState = useProductReviews(
    product?.id != null ? Number(product.id) : null,
  );

  const finalCtaRef = useRef<HTMLDivElement | null>(null);

  // Fetch product
  useEffect(() => {
    if (!productId || !tenant?.id) return;
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const p = await productsAPI.getById(Number(productId), tenant.id);
        if (!alive) return;
        setProduct(p);
        setErr(null);
      } catch (e: any) {
        if (alive) setErr(e?.message || 'Could not load product');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [productId, tenant?.id]);

  // SEO
  useEffect(() => {
    if (!product) return;
    const images = productImages(product);
    setProductSEO({
      productId: product.id,
      productName: product.name,
      productSlug: (product as any).slug || String(product.id),
      description: product.description,
      imageUrl: images[0],
      price: productPrice(product),
      currency: (product as any).currency || 'MYR',
      inStock:
        ((product as any).stock ?? 1) > 0 && (product as any).active !== false,
      brandName: tenant?.name,
      storeName: tenant?.name,
      averageRating: reviewsState.averageRating ?? undefined,
      ratingCount: reviewsState.totalCount,
      reviewCount: reviewsState.totalCount,
    });
    return () => clearProductSEO();
  }, [product, tenant, reviewsState.averageRating, reviewsState.totalCount]);

  const images = useMemo(
    () => (product ? productImages(product) : []),
    [product],
  );
  const price = product ? productPrice(product) : 0;
  const currency = (product as any)?.currency || 'MYR';
  const stock = (product as any)?.stock as number | undefined;
  const inStock = (stock ?? 1) > 0 && product?.status !== 'inactive';
  const benefits = product ? buildDefaultBenefits(product) : [];

  const handleAdd = (opts?: { thenScroll?: boolean }) => {
    if (!product || !inStock) return;
    addItem(product, 1);
    showToast?.('Added to cart', 'success');
    if (opts?.thenScroll) {
      finalCtaRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  const handleBuyNow = () => {
    if (!product || !inStock) return;
    addItem(product, 1);
    navigate('/cart');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-sm text-neutral-500">
          Loading…
        </div>
      </div>
    );
  }

  if (err || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="text-xl font-semibold mb-2">
            We couldn't load this product
          </h1>
          <p className="text-sm text-neutral-500">{err || 'Try refreshing the page.'}</p>
        </div>
      </div>
    );
  }

  const primaryColor =
    (tenant as any)?.branding?.primaryColor ||
    (tenant as any)?.config?.colors?.primary ||
    'var(--primary-color, #0F766E)';

  const tenantLogo =
    (tenant as any)?.config?.customLogo ||
    (tenant as any)?.branding?.logoUrl ||
    null;

  // Null unless this is a subscription — see ProductTypeSpecs.
  const billingInterval = subscriptionInterval(product);

  return (
    <main
      className="min-h-screen pb-32 sm:pb-12"
      style={{ background: 'var(--bg, #fafafa)' }}
    >
      {/* MINIMAL NAVBAR — logo + cart only */}
      <header
        className="sticky top-0 z-30 backdrop-blur"
        style={{
          background: 'rgba(255,255,255,0.92)',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 rounded"
            aria-label={tenant?.name || 'Home'}
          >
            {tenantLogo ? (
              <img
                src={tenantLogo}
                alt={tenant?.name || ''}
                className="h-7 sm:h-8 w-auto object-contain"
              />
            ) : (
              <span
                className="text-base sm:text-lg font-bold tracking-tight"
                style={{ color: 'var(--card-text, #111827)' }}
              >
                {tenant?.name || 'Store'}
              </span>
            )}
          </Link>
          <Link
            to="/cart"
            aria-label={`Cart (${cartCount} item${cartCount === 1 ? '' : 's'})`}
            className="relative p-2 rounded-full hover:bg-black/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
              style={{ color: 'var(--card-text, #111827)' }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            {cartCount > 0 && (
              <span
                aria-hidden
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center"
                style={{ background: primaryColor }}
              >
                {cartCount}
              </span>
            )}
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-12 items-start">
          {/* Left: gallery */}
          <div className="space-y-3">
            <div
              className="relative aspect-square rounded-3xl overflow-hidden"
              style={{ background: '#f3f4f6' }}
            >
              {images[selectedImage] && (
                <img
                  src={images[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-contain cursor-zoom-in"
                  onClick={() => setLightboxOpen(true)}
                />
              )}
              {images.length > 1 && (
                <div className="absolute bottom-3 left-3 px-3 py-1 rounded-full bg-black/70 text-white text-xs font-medium">
                  {selectedImage + 1} / {images.length}
                </div>
              )}
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-5 gap-2">
                {images.slice(0, 5).map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    aria-label={`View image ${i + 1}`}
                    className={`aspect-square rounded-xl overflow-hidden border-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                      selectedImage === i
                        ? 'border-[var(--primary-color,#0F766E)]'
                        : 'border-transparent hover:border-neutral-300'
                    }`}
                  >
                    <img
                      src={src}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: title + price + CTA */}
          <div className="space-y-5">
            {reviewsState.totalCount > 0 && (
              <div className="flex items-center gap-2">
                <div className="flex items-center" aria-hidden>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <span
                      key={n}
                      style={{
                        color:
                          n <= Math.round(reviewsState.averageRating ?? 0)
                            ? '#fbbf24'
                            : '#e5e7eb',
                      }}
                      className="text-base"
                    >
                      ★
                    </span>
                  ))}
                </div>
                <span className="text-xs text-[color:var(--card-muted,#6b7280)]">
                  {(reviewsState.averageRating ?? 0).toFixed(1)} ·{' '}
                  {reviewsState.totalCount} review
                  {reviewsState.totalCount === 1 ? '' : 's'}
                </span>
              </div>
            )}

            <h1
              className="text-3xl sm:text-4xl font-bold leading-tight"
              style={{ color: 'var(--card-text, #111827)' }}
            >
              {product.name}
            </h1>

            <div className="flex items-baseline gap-3 flex-wrap">
              <span
                className="text-3xl font-bold"
                style={{ color: 'var(--card-text, #111827)' }}
              >
                {fmt(price, currency)}
                {billingInterval && (
                  <span style={{ fontSize: '0.5em', fontWeight: 600, opacity: 0.75, marginLeft: 2 }}>
                    /{billingInterval}
                  </span>
                )}
              </span>
              {(product as any).originalPrice &&
                (product as any).originalPrice > price && (
                  <span className="text-base line-through text-[color:var(--card-muted,#9ca3af)]">
                    {fmt((product as any).originalPrice, currency)}
                  </span>
                )}
            </div>

            <ProductTypeSpecs product={product} />

            {typeof stock === 'number' && (
              <StockUrgency stock={stock} />
            )}

            {/* Short description (1-2 lines) — hero copy */}
            {product.description && product.description.length >= 60 && (
              <p
                className="text-sm sm:text-base leading-relaxed"
                style={{ color: 'var(--card-text-soft, #374151)' }}
              >
                {product.description.slice(0, 220)}
                {product.description.length > 220 ? '…' : ''}
              </p>
            )}

            <div className="space-y-3">
              <button
                type="button"
                onClick={() => handleAdd()}
                disabled={!inStock}
                className="w-full rounded-full py-4 text-base font-semibold text-white transition-transform hover:scale-[0.99] active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{
                  background: primaryColor,
                  minHeight: '44px',
                }}
              >
                {inStock ? 'Add to cart' : 'Out of stock'}
              </button>
              {inStock && (
                <button
                  type="button"
                  onClick={handleBuyNow}
                  className="w-full rounded-full py-3 text-sm font-semibold border-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  style={{
                    borderColor: primaryColor,
                    color: primaryColor,
                    minHeight: '44px',
                  }}
                >
                  Buy now
                </button>
              )}
            </div>

            <TrustRow className="!justify-start" />
          </div>
        </div>
      </section>

      {/* PHOTO REVIEWS — above-fold for trust */}
      {reviewsState.totalCount > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-12 sm:mt-16">
          <PhotoReviewsPreview
            reviews={reviewsState.reviews as any}
            averageRating={reviewsState.averageRating ?? undefined}
            totalCount={reviewsState.totalCount}
          />
        </section>
      )}

      {/* BENEFITS */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 mt-14 sm:mt-20">
        <h2
          className="text-2xl sm:text-3xl font-bold mb-6"
          style={{ color: 'var(--card-text, #111827)' }}
        >
          Why you'll love it
        </h2>
        <ul className="space-y-3">
          {(benefits.length > 0
            ? benefits
            : [
                'Premium-quality build, made to last',
                'Fast, tracked shipping nationwide',
                '30-day satisfaction guarantee',
              ]
          ).map((b, i) => (
            <li key={i} className="flex items-start gap-3">
              <span
                aria-hidden
                className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-sm font-bold mt-0.5"
                style={{ background: primaryColor }}
              >
                ✓
              </span>
              <span
                className="text-base leading-relaxed"
                style={{ color: 'var(--card-text-soft, #374151)' }}
              >
                {b}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* BUNDLE & SAVE — merchant-priced set. Spacing goes on the component,
          not a wrapper section: the widget self-hides, and a wrapper would
          leave its margin behind as an empty band. */}
      <BundleAndSave product={product} className="px-4 sm:px-6 mt-14 sm:mt-20" />

      {/* FREQUENTLY BOUGHT TOGETHER */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 mt-14 sm:mt-20">
        <FrequentlyBoughtTogether product={product} />
      </section>

      {/* TRUST */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 mt-14 sm:mt-20">
        <TrustBadgesSection
          config={{
            layout: 'row',
            showBnpl: false,
          } as any}
        />
        <PaymentIconsSection
          config={{
            align: 'center',
            layout: 'row',
            showLockNote: true,
          } as any}
        />
      </section>

      {/* FAQ */}
      <section className="max-w-2xl mx-auto px-4 sm:px-6 mt-14 sm:mt-20">
        <h2
          className="text-2xl sm:text-3xl font-bold mb-6 text-center"
          style={{ color: 'var(--card-text, #111827)' }}
        >
          Common questions
        </h2>
        <div className="space-y-3">
          {DEFAULT_FAQS.map((f, i) => {
            const open = openFaq === i;
            return (
              <div
                key={i}
                className="rounded-2xl border overflow-hidden"
                style={{ borderColor: 'rgba(0,0,0,0.08)' }}
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(open ? null : i)}
                  aria-expanded={open}
                  className="w-full text-left px-5 py-4 flex items-center justify-between gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  style={{ color: 'var(--card-text, #111827)' }}
                >
                  <span className="font-semibold text-sm sm:text-base">
                    {f.q}
                  </span>
                  <span
                    aria-hidden
                    className="text-xl transition-transform"
                    style={{
                      transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
                      color: 'var(--card-muted, #6b7280)',
                    }}
                  >
                    +
                  </span>
                </button>
                {open && (
                  <div
                    className="px-5 pb-4 text-sm leading-relaxed"
                    style={{ color: 'var(--card-text-soft, #374151)' }}
                  >
                    {f.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Optional review form */}
      {product?.id != null && (
        <section className="max-w-2xl mx-auto px-4 sm:px-6 mt-14 sm:mt-20">
          <SubmitReviewForm
            productId={Number(product.id)}
            onSubmitted={reviewsState.refetch}
          />
        </section>
      )}

      {/* FINAL CTA */}
      <section
        ref={finalCtaRef}
        className="max-w-3xl mx-auto px-4 sm:px-6 mt-14 sm:mt-20"
      >
        <div
          className="rounded-3xl p-8 sm:p-12 text-center"
          style={{
            background:
              'color-mix(in srgb, var(--primary-color, #0F766E) 8%, transparent)',
          }}
        >
          <h2
            className="text-2xl sm:text-3xl font-bold mb-2"
            style={{ color: 'var(--card-text, #111827)' }}
          >
            Ready to get yours?
          </h2>
          <p
            className="text-sm sm:text-base mb-6"
            style={{ color: 'var(--card-text-soft, #374151)' }}
          >
            {fmt(price, currency)} · Free returns within 30 days
          </p>
          <button
            type="button"
            onClick={handleBuyNow}
            disabled={!inStock}
            className="inline-block rounded-full px-10 py-4 text-base sm:text-lg font-semibold text-white transition-transform hover:scale-[0.99] active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            style={{
              background: primaryColor,
              minWidth: '220px',
              minHeight: '44px',
            }}
          >
            {inStock ? 'Buy now' : 'Out of stock'}
          </button>
        </div>
      </section>

      {/* Sticky mobile CTA */}
      {inStock && (
        <div
          className="fixed bottom-0 inset-x-0 sm:hidden z-40 px-4 py-3 border-t backdrop-blur"
          style={{
            background: 'rgba(255,255,255,0.94)',
            borderColor: 'rgba(0,0,0,0.06)',
            paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))',
          }}
        >
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div
                className="text-xs truncate"
                style={{ color: 'var(--card-muted, #6b7280)' }}
              >
                {product.name}
              </div>
              <div
                className="text-base font-bold"
                style={{ color: 'var(--card-text, #111827)' }}
              >
                {fmt(price, currency)}
              </div>
            </div>
            <button
              type="button"
              onClick={handleBuyNow}
              className="rounded-full px-6 py-3 font-semibold text-white whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              style={{ background: primaryColor, minHeight: '44px' }}
            >
              Buy now
            </button>
          </div>
        </div>
      )}

      <ImageLightbox
        images={images}
        startIndex={selectedImage}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onIndexChange={setSelectedImage}
        alt={product.name}
      />
    </main>
  );
};

export default SingleProductPage;
